<template>
  <MeteringPointCard :metering-point :show-title>
    <LoadingWrapper :error="loadingFailed" :loading="loading">
      <MessageComponent v-if="isEmpty" color="info">
        Geen metingen beschikbaar in deze periode.
      </MessageComponent>
      <template v-else>
        <!-- Stat row -->
        <div v-if="showStats" class="flex justify-between gap-4">
          <StatComponent
            label="Hoogste vermogen"
            :value="powerStats.highest"
            :precision="2"
            unit="W"
            class="flex-1"
          >
            <template #icon>
              <BaseIcon name="arrow-up" class="fill-gray" />
            </template>
          </StatComponent>
          <StatComponent
            label="Laagste vermogen"
            :value="powerStats.lowest"
            :precision="2"
            unit="W"
            class="flex-1"
          >
            <template #icon>
              <BaseIcon name="arrow-down" class="fill-gray" />
            </template>
          </StatComponent>
        </div>
        <!-- Chart -->
        <div :style="{ height: 350 + annotationData.length * 24 + 'px' }">
          <TimeseriesChart
            chart-type="area"
            :series="series"
            :axes="axes"
            :data="chartData"
            :x-limits="[dateRange[0], endOfDay(dateRange[1])]"
            :aligned-axes="false"
          />
        </div>
      </template>
    </LoadingWrapper>
  </MeteringPointCard>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { differenceInCalendarDays, endOfDay } from "date-fns"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import useNotificationStore from "@/stores/notificationStore.js"
import {
  BaseIcon,
  LoadingWrapper,
  TimeseriesChart,
} from "@repowerednl/ui-component-library"
import { ALLOCATION_POINT } from "@/stores/repower/meteringPointStoreFactories.js"
import MeteringPointCard from "@/components/panels/MeteringPointCard.vue"
import MessageComponent from "@/components/information/MessageComponent.vue"
import StatComponent from "@/components/information/StatComponent.vue"

const props = defineProps({
  meteringPoint: {
    type: Object,
    default: null,
  },
  dateRange: {
    type: Array,
    required: true,
  },
  showStats: Boolean,
  showTitle: Boolean,
})

const DAYS_1MIN = 2
const DAYS_15MIN = 15
const DAYS_1H = 32
const LIMIT_DASH_PATTERN = [6, 4]

const notificationStore = useNotificationStore()
const chartData = ref({})
const loading = ref(false)
const loadingFailed = ref(false)
const annotationData = ref([])

const axes = {
  energy: {
    title: "Vermogen",
    unit: "W",
    minPrefix: "k",
    suggestedMin: -10000,
    suggestedMax: 10000,
  },
}

const series = {
  total: {
    label: "Vermogen energy hub",
    axis: "energy",
    rescale: 1000,
    color: "gray",
    type: "line",
  },
  limitTakeOff: {
    label: "Afnamelimiet",
    axis: "energy",
    rescale: 1000,
    color: "secondary",
    type: "line",
    options: { borderDash: LIMIT_DASH_PATTERN },
  },
  limitFeedIn: {
    label: "Terugleverlimiet",
    axis: "energy",
    rescale: 1000,
    color: "secondary",
    type: "line",
    options: { borderDash: LIMIT_DASH_PATTERN },
  },
  participant: {
    label: "Vermogen deelnemer",
    axis: "energy",
    rescale: 1000,
    color: "energy",
  },
}

// Backend frequencies per spec: 1min, 15min, 1h, 1D
function getFreq(dateRange) {
  const days = 1 + differenceInCalendarDays(dateRange[1], dateRange[0])
  if (days < DAYS_1MIN) {
    return "1min"
  } else if (days < DAYS_15MIN) {
    return "15min"
  } else if (days < DAYS_1H) {
    return "1h"
  } else {
    return "1D"
  }
}

const isEmpty = computed(
  () =>
    !loading.value &&
    !loadingFailed.value &&
    Object.keys(chartData.value).length === 0,
)

const powerStats = computed(() => {
  let min = Infinity
  let max = -Infinity
  let seen = false
  for (const entry of Object.values(chartData.value)) {
    // Hub total power (matches the gray Energy Hub line in the chart)
    const value = entry?.total
    if (value !== undefined && value !== null) {
      seen = true
      if (value < min) {
        min = value
      }
      if (value > max) {
        max = value
      }
    }
  }
  if (!seen) {
    return { highest: Number.NaN, lowest: Number.NaN }
  }
  return { highest: max * 1000, lowest: min * 1000 }
})

function addLimitChartData(serie, value, dateRange) {
  const keyStart = dateRange[0].toISOString()
  const keyStop = endOfDay(dateRange[1]).toISOString()
  chartData.value[keyStart] = {
    ...chartData.value[keyStart],
    [serie]: value,
  }
  chartData.value[keyStop] = {
    ...chartData.value[keyStop],
    [serie]: value,
  }
}

function onLoadEnergyHubFailed(error) {
  loadingFailed.value = true
  loading.value = false
  notificationStore.pushError(
    "Fout bij het ophalen van metingen",
    `De metingen van de Energy Hub konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
    "load-energy-hub-data-error",
  )
}

const loadData = debounce((meteringPoint, dateRange) => {
  meteringPoint.loadEnergyHub(
    getFreq(dateRange),
    dateRange,
    (data) => {
      chartData.value = data.measurements ?? {}
      addLimitChartData("limitTakeOff", -data.limit_take_off, dateRange)
      addLimitChartData("limitFeedIn", data.limit_feed_in, dateRange)
      loading.value = false
    },
    onLoadEnergyHubFailed,
  )
}, DEBOUNCE_DELAY)

watch(
  [() => props.meteringPoint, () => props.dateRange],
  ([meteringPoint, dateRange]) => {
    chartData.value = {}
    annotationData.value = []
    if (meteringPoint?.role !== ALLOCATION_POINT) {
      loading.value = false
      return
    }
    loading.value = true
    loadingFailed.value = false
    loadData(meteringPoint, dateRange)
  },
  { immediate: true },
)
</script>
